import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  ChartBarIcon, CheckCircleIcon, ExclamationTriangleIcon,
  CalendarDaysIcon, ClipboardDocumentCheckIcon, EyeIcon, AcademicCapIcon,
  CurrencyDollarIcon, FlagIcon, PlusIcon, TrashIcon, PencilIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { DATE_FLOOR } from '../../lib/dateBounds';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const formatMonth = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '';
const dollars = (cents) => cents == null ? '—' : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function ReportsPage() {
  return (
    <div className="animate-slide-up space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ChartBarIcon className="w-6 h-6" /> Reports & KPIs
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Cadence compliance, trends, and goals across the center.
        </p>
      </header>

      <CompliancePanel />
      <StaffScorecardPanel />
      <TrendsPanel />
      <RevenuePanel />
      <TeamGoalsPanel />
      <PeopleGoalsPanel />
    </div>
  );
}

// ─── Cadence Compliance ────────────────────────────────────

function CompliancePanel() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/reports/compliance')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load compliance'));
  }, []);

  if (!data) return <Spinner />;

  const cards = [
    { key: 'one_on_ones',         label: '1:1 Meetings',     icon: ClipboardDocumentCheckIcon, link: '/one-on-ones',    data: data.one_on_ones },
    { key: 'observations',        label: 'Observations',     icon: EyeIcon,                    link: '/observations',   data: data.observations },
    { key: 'annual_reviews',      label: 'Annual Reviews',   icon: AcademicCapIcon,            link: '/performance',    data: data.annual_reviews },
    { key: 'ninety_day_reviews',  label: '90-day Reviews',   icon: CalendarDaysIcon,           link: '/performance',    data: data.ninety_day_reviews },
    { key: 'onboarding',          label: 'Onboarding',       icon: ClipboardDocumentCheckIcon, link: '/onboarding',     data: data.onboarding },
  ];

  return (
    <section>
      <SectionHeader title="Cadence Compliance" subtitle="Are we doing what we said we'd do?" />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map(c => {
          const tone = c.data.percent >= 90 ? 'green' : c.data.percent >= 70 ? 'amber' : 'red';
          const isOpen = expanded === c.key;
          return (
            <div key={c.key} className={`bg-white rounded-2xl border border-gray-200 p-4 ${isOpen ? 'lg:col-span-5' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <c.icon className="w-4 h-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-700">{c.label}</span>
                </div>
                <Link to={c.link} className="text-xs text-gray-400 hover:text-orange-500">↗</Link>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`text-3xl font-bold ${tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-600' : 'text-green-600'}`}>
                  {c.data.percent}%
                </span>
                <span className="text-xs text-gray-500">{c.data.on_target} / {c.data.total}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{c.data.expectation}</p>

              {c.data.behind > 0 && (
                <button onClick={() => setExpanded(isOpen ? null : c.key)}
                  className="mt-2 text-xs text-orange-600 hover:underline">
                  {isOpen ? 'Collapse' : `${c.data.behind} behind →`}
                </button>
              )}

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Behind on {c.label.toLowerCase()}:</p>
                  <ul className="space-y-1">
                    {c.data.behind_staff.map(s => (
                      <li key={s.id} className="text-xs text-gray-600">
                        {s.first_name} {s.last_name}
                        {s.last_date && <span className="text-gray-400"> · last {formatDate(s.last_date)}</span>}
                        {!s.last_date && <span className="text-red-400"> · never</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Per-staff Scorecard ──────────────────────────────────

function StaffScorecardPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('points_balance');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [pointsFilter, setPointsFilter] = useState(''); // '', '>0', '>2', '>5'

  useEffect(() => {
    api.get('/reports/staff-scorecard')
      .then(({ data }) => setRows(data))
      .catch(() => toast.error('Failed to load scorecard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const filtered = rows.filter(s => {
    if (filterRole && s.role !== filterRole) return false;
    if (pointsFilter === '>0' && (s.points_balance ?? 0) <= 0) return false;
    if (pointsFilter === '>2' && (s.points_balance ?? 0) <= 2) return false;
    if (pointsFilter === '>5' && (s.points_balance ?? 0) <= 5) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${s.first_name || ''} ${s.last_name || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  const sort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const Th = ({ k, children }) => (
    <th onClick={() => sort(k)}
      className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700">
      {children} {sortKey === k && (sortDir === 'asc' ? '↑' : '↓')}
    </th>
  );

  return (
    <section>
      <SectionHeader title="Staff Scorecard" subtitle={`${filtered.length} of ${rows.length} active staff — sortable`} />
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-orange-500 focus:outline-none" />
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
          <option value="">All roles</option>
          <option value="director">Director</option>
          <option value="admin">Office Admin</option>
          <option value="teacher">Full-Time Teacher</option>
          <option value="float">Part-Time Float</option>
          <option value="cook">Cook</option>
        </select>
        <select value={pointsFilter} onChange={(e) => setPointsFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
          <option value="">Any points</option>
          <option value=">0">Has points (&gt;0)</option>
          <option value=">2">Watch list (&gt;2 pts)</option>
          <option value=">5">Action zone (&gt;5 pts)</option>
        </select>
        {(search || filterRole || pointsFilter) && (
          <button onClick={() => { setSearch(''); setFilterRole(''); setPointsFilter(''); }}
            className="text-xs text-gray-500 hover:text-orange-500 px-2">Clear</button>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100">
              <tr>
                <Th k="last_name">Staff</Th>
                <Th k="points_balance">Pts</Th>
                <Th k="last_oneonone">Last 1:1</Th>
                <Th k="last_observation">Last Obs</Th>
                <Th k="avg_observation_score">Avg Obs</Th>
                <Th k="last_review">Last Review</Th>
                <Th k="open_ai_tasks">AI Tasks</Th>
                <Th k="open_disciplinary">Open Disc.</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <Link to={`/staff/${s.id}`} className="font-medium text-gray-900 hover:text-orange-500">
                      {s.first_name} {s.last_name}
                    </Link>
                    <p className="text-xs text-gray-400">{s.role?.replace(/_/g, ' ')}</p>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      s.points_balance > 5 ? 'bg-red-50 text-red-700'
                        : s.points_balance > 2 ? 'bg-amber-50 text-amber-700'
                        : 'bg-green-50 text-green-700'
                    }`}>
                      {Number(s.points_balance).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">{formatDate(s.last_oneonone)}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{formatDate(s.last_observation)}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {s.avg_observation_score ? `${Number(s.avg_observation_score).toFixed(1)}/5` : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">{formatDate(s.last_review)}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{s.open_ai_tasks}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {s.open_disciplinary > 0
                      ? <span className="text-red-600 font-semibold">{s.open_disciplinary}</span>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ─── Trends ────────────────────────────────────────────────

function TrendsPanel() {
  const [trends, setTrends] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    api.get(`/reports/trends?months=${months}`)
      .then(({ data }) => setTrends(data))
      .catch(() => toast.error('Failed to load trends'));
  }, [months]);

  if (!trends) return <Spinner />;

  return (
    <section>
      <SectionHeader title="Trends"
        subtitle={`Last ${months} months`}
        action={
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
            className="text-xs rounded-lg border border-gray-200 px-2 py-1 bg-white">
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Attendance points">
          <BarChart data={trends.attendance.map(d => ({ ...d, month: formatMonth(d.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="infractions" fill="#ef4444" name="Infractions" />
            <Bar dataKey="credits" fill="#10b981" name="Credits" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Observation scores">
          <LineChart data={trends.observations.map(d => ({ ...d, month: formatMonth(d.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Line type="monotone" dataKey="avg_score" stroke="#f97316" strokeWidth={2} name="Avg score" />
          </LineChart>
        </ChartCard>

        <ChartCard title="Recruitment funnel">
          <BarChart data={trends.recruitment.map(d => ({ ...d, month: formatMonth(d.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="applications" fill="#a78bfa" name="Applied" />
            <Bar dataKey="interviewed" fill="#60a5fa" name="Interviewed" />
            <Bar dataKey="hired" fill="#10b981" name="Hired" />
          </BarChart>
        </ChartCard>
      </div>
    </section>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <h3 className="text-xs font-semibold text-gray-700 mb-2">{title}</h3>
      <ResponsiveContainer width="100%" height={220}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

// ─── Revenue ──────────────────────────────────────────────

function RevenuePanel() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { register, handleSubmit, reset } = useForm();

  const load = useCallback(() => {
    api.get('/revenue?months=12')
      .then(({ data }) => setRows(data))
      .catch(() => toast.error('Failed to load revenue'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (form) => {
    try {
      await api.put('/revenue', {
        month: form.month + '-01',
        revenue_dollars: form.revenue_dollars ? Number(form.revenue_dollars) : null,
        goal_dollars: form.goal_dollars ? Number(form.goal_dollars) : null,
        notes: form.notes || null,
      });
      toast.success('Saved');
      setShowForm(false); reset(); load();
    } catch { toast.error('Failed to save'); }
  };

  const totalRev = rows.reduce((s, r) => s + (r.revenue_cents || 0), 0);
  const totalGoal = rows.reduce((s, r) => s + (r.goal_cents || 0), 0);
  const onPace = totalGoal > 0 ? Math.round((totalRev / totalGoal) * 100) : null;

  const chartData = rows.map(r => ({
    month: formatMonth(r.month),
    revenue: r.revenue_cents != null ? r.revenue_cents / 100 : null,
    goal: r.goal_cents != null ? r.goal_cents / 100 : null,
  }));

  return (
    <section>
      <SectionHeader title="Revenue"
        subtitle={onPace != null ? `YTD pacing: ${onPace}% of goal (${dollars(totalRev)} / ${dollars(totalGoal)})` : 'Manual entry until bbc-booking sync is wired'}
        action={
          <button onClick={() => setShowForm(true)}
            className="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 inline-flex items-center gap-1">
            <PlusIcon className="w-3.5 h-3.5" /> Add / Edit Month
          </button>
        }
      />
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        {rows.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No revenue data yet — click "Add / Edit Month" to start.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="goal" fill="#cbd5e1" name="Goal" />
              <Bar dataKey="revenue" fill="#10b981" name="Actual" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {showForm && (
        <Modal title="Set monthly revenue" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit(submit)} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
              <input type="month" {...register('month', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goal ($)</label>
                <input type="number" step="0.01" {...register('goal_dollars')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actual ($)</label>
                <input type="number" step="0.01" {...register('revenue_dollars')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea {...register('notes')} rows={2}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">Save</button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

// ─── Team Goals ───────────────────────────────────────────

function TeamGoalsPanel() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset } = useForm();

  const load = useCallback(() => {
    api.get('/team-goals').then(({ data }) => setRows(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (g = null) => {
    setEditing(g);
    if (g) {
      reset({
        title: g.title, description: g.description, category: g.category,
        target_value: g.target_value, target_date: g.target_date?.slice(0, 10),
        progress_notes: g.progress_notes,
      });
    } else reset({});
    setShowForm(true);
  };

  const submit = async (form) => {
    try {
      if (editing) {
        await api.patch(`/team-goals/${editing.id}`, form);
      } else {
        await api.post('/team-goals', form);
      }
      toast.success(editing ? 'Updated' : 'Added');
      setShowForm(false); setEditing(null); reset(); load();
    } catch { toast.error('Failed'); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/team-goals/${id}`, { status }); load(); } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try { await api.delete(`/team-goals/${id}`); load(); } catch { toast.error('Failed'); }
  };

  const tone = (s) => ({ open: 'gray', in_progress: 'blue', met: 'green', missed: 'red', dropped: 'gray' }[s] || 'gray');
  const toneClass = (t) => ({
    gray: 'bg-gray-100 text-gray-600', blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700', red: 'bg-red-100 text-red-700',
  }[t]);

  return (
    <section>
      <SectionHeader title="Team Goals" subtitle={`${rows.length} total`}
        action={
          <button onClick={() => open(null)}
            className="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 inline-flex items-center gap-1">
            <PlusIcon className="w-3.5 h-3.5" /> Add Team Goal
          </button>
        }
      />

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center text-sm text-gray-400">
          No team goals yet. Add your first to track center-wide commitments.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map(g => (
            <div key={g.id} className="bg-white rounded-2xl border border-gray-200 p-4 group">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{g.title}</p>
                  {g.description && <p className="text-xs text-gray-500 mt-1">{g.description}</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${toneClass(tone(g.status))}`}>
                  {g.status?.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
                {g.target_value && <span><FlagIcon className="w-3 h-3 inline mr-1" />{g.target_value}</span>}
                {g.target_date && <span><CalendarDaysIcon className="w-3 h-3 inline mr-1" />Due {formatDate(g.target_date)}</span>}
                {g.owner_first && <span>Owner: {g.owner_first} {g.owner_last || ''}</span>}
                <span className="ml-auto text-gray-300">{g.category}</span>
              </div>
              <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                {['open', 'in_progress', 'met', 'missed', 'dropped'].map(s => (
                  <button key={s} onClick={() => setStatus(g.id, s)}
                    className={`text-xs px-2 py-0.5 rounded ${g.status === s ? toneClass(tone(s)) : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                    {s.replace(/_/g, ' ')}
                  </button>
                ))}
                <button onClick={() => open(g)} className="ml-auto text-gray-400 hover:text-gray-700"><PencilIcon className="w-4 h-4" /></button>
                <button onClick={() => remove(g.id)} className="text-gray-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <Modal title={editing ? 'Edit Team Goal' : 'New Team Goal'} onClose={() => { setShowForm(false); setEditing(null); }}>
          <form onSubmit={handleSubmit(submit)} className="space-y-3">
            <Input label="Title *" {...register('title', { required: true })} />
            <Textarea label="Description" rows={2} {...register('description')} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select {...register('category')} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white">
                  <option value="general">General</option>
                  <option value="retention">Retention</option>
                  <option value="quality">Quality</option>
                  <option value="growth">Growth</option>
                  <option value="training">Training</option>
                  <option value="financial">Financial</option>
                </select>
              </div>
              <Input label="Target Date" type="date" min={DATE_FLOOR} {...register('target_date')} />
            </div>
            <Input label="Target value" placeholder="e.g. 12 hires by Q4, 95% retention" {...register('target_value')} />
            <Textarea label="Progress notes" rows={2} {...register('progress_notes')} />
            <div className="flex gap-2 pt-2">
              <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">
                {editing ? 'Save' : 'Add Goal'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

// ─── People Goals (1:1 + review) ──────────────────────────

function PeopleGoalsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/reports/goals').then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return <Spinner />;

  return (
    <section>
      <SectionHeader title="People Goals" subtitle="From 1:1s and annual reviews" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Open 1:1 Goals</h3>
          {data.one_on_one_goals.length === 0 ? (
            <p className="text-sm text-gray-400">No open 1:1 goals.</p>
          ) : (
            <ul className="space-y-2 max-h-80 overflow-y-auto">
              {data.one_on_one_goals.map(g => (
                <li key={g.id} className="text-sm border-l-2 border-blue-200 pl-3">
                  <p className="text-gray-900">{g.body}</p>
                  <p className="text-xs text-gray-500">{g.first_name} {g.last_name} · {formatDate(g.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Annual Review Goals</h3>
          {data.review_goals.length === 0 ? (
            <p className="text-sm text-gray-400">No recent review goals.</p>
          ) : (
            <ul className="space-y-3 max-h-80 overflow-y-auto">
              {data.review_goals.map(r => (
                <li key={r.id} className="text-sm border-l-2 border-amber-200 pl-3">
                  <p className="font-medium text-gray-900">{r.first_name} {r.last_name} <span className="text-xs text-gray-500">· {r.review_period}</span></p>
                  {r.career_goals && <p className="text-xs text-gray-700 mt-1"><strong>Career:</strong> <span className="whitespace-pre-line">{r.career_goals}</span></p>}
                  {r.personal_goals && <p className="text-xs text-gray-700 mt-1"><strong>Personal:</strong> <span className="whitespace-pre-line">{r.personal_goals}</span></p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Shared bits ──────────────────────────────────────────

function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Spinner() {
  return <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;
}

function Input({ label, ...props }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input {...props} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <textarea {...props} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
